'use client';

import { useState } from 'react';
import { Loader2, ArrowRight, ChevronDown } from 'lucide-react';
import type { AddressResult } from '@/lib/web3/types';
import ResultPanel from '@/components/web3/ResultPanel';

const VERDICT_LABEL: Record<AddressResult['verdict'], string> = {
  safe: 'Address looks fine',
  caution: 'Double-check this',
  danger: 'Do not send',
};

export default function AddressCheck() {
  const [address, setAddress] = useState('');
  const [compareTo, setCompareTo] = useState('');
  const [showCompare, setShowCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<AddressResult | null>(null);

  async function run() {
    const value = address.trim();
    if (!value) {
      setError('Paste a Solana address.');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch('/api/address-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: value, compareTo: compareTo.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || 'Check failed. Try again.');
        return;
      }
      setResult(data as AddressResult);
    } catch {
      setError('Network error. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative rounded-none border border-white/10 bg-neutral-900/40 p-6 sm:p-10">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void run();
        }}
        className="max-w-2xl mx-auto"
      >
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="Solana address you're about to send to"
          className="w-full h-12 px-4 rounded-none bg-black/50 border border-white/10 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-rose-500/50 transition-colors font-mono"
          aria-label="Solana address"
          autoComplete="off"
          spellCheck={false}
        />

        <button
          type="button"
          onClick={() => setShowCompare((v) => !v)}
          className="mt-3 inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white transition-colors"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showCompare ? 'rotate-180' : ''}`} />
          Check for address poisoning (paste the address you normally use)
        </button>

        {showCompare && (
          <input
            type="text"
            value={compareTo}
            onChange={(e) => setCompareTo(e.target.value)}
            placeholder="Your known / reference address (optional)"
            className="mt-2 w-full h-12 px-4 rounded-none bg-black/50 border border-white/10 text-white text-sm placeholder:text-neutral-600 focus:outline-none focus:border-rose-500/50 transition-colors font-mono"
            aria-label="Reference address"
            autoComplete="off"
            spellCheck={false}
          />
        )}

        <button
          type="submit"
          disabled={loading}
          className="mt-3 w-full inline-flex items-center justify-center h-12 px-7 rounded-none bg-white text-black text-sm font-semibold hover:bg-neutral-200 transition-colors gap-2 disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Inspect address <ArrowRight className="w-4 h-4" /></>}
        </button>

        {error && <p className="mt-3 text-sm text-rose-400">{error}</p>}
      </form>

      {result && (
        <ResultPanel
          verdict={result.verdict}
          score={result.score}
          label={VERDICT_LABEL[result.verdict]}
          subtitle={result.address}
          signals={result.signals}
        >
          {result.onchain?.available && result.onchain.exists && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-[10px] uppercase tracking-wider text-neutral-500">On-chain:</span>
              {result.onchain.executable ? (
                <span className="inline-flex items-center gap-1.5 border border-rose-500/40 bg-rose-500/10 px-2.5 py-1 text-rose-300">
                  Program account (not a wallet)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 border border-white/10 bg-black/30 px-2.5 py-1 text-neutral-300 font-mono tabular-nums">
                  {result.onchain.solBalance.toLocaleString('en-US', { maximumFractionDigits: 4 })} SOL
                </span>
              )}
            </div>
          )}
        </ResultPanel>
      )}

      {result && (
        <p className="mt-4 max-w-2xl mx-auto text-xs text-neutral-600">
          Format, pattern and on-chain check. A valid address does not verify ownership or
          trust - always confirm the recipient through a trusted channel.
        </p>
      )}
    </div>
  );
}
